import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Stack,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  History as HistoryIcon,
  RemoveCircleOutline as RemoveItemIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import DeliverActionIcon from '../icons/DeliverActionIcon';
import { Link as RouterLink } from 'react-router-dom';
import CertificateLink from '../CertificateLink';
import { CHECK_STATUSES, DIAMOND_TYPES, ORDER_STATUSES } from '../../constants/appConstants';
import CheckBalanceCards from './CheckBalanceCards';
import CheckDiscountEditor from './CheckDiscountEditor';
import { getCheckPrepaymentHint } from '../../utils/checkBalanceDisplay';
import { usePageNotice } from '../../context/FeedbackContext';
import CheckExchangeRatesEditor from './CheckExchangeRatesEditor';
import CheckTransactionsTable from './CheckTransactionsTable';
import {
  getItemCarat,
  getItemDisplayPrice,
  getItemPricePerCt,
  resolveItemCurrencyCode,
  getItemRef,
  getItemLab,
  getItemDetails,
  getOrderItemDescriptionCell,
  getCertifiedDiamondSpecsWithWeight,
  getItemTypeLabel,
  getOrderId,
  getCashieringCheckItemPrimaryLabel,
  isCustomByWeightItem,
} from '../../utils/orderItemDisplay';
import { weightUnitShort } from '../../utils/customItemPricing';
import { getCurrencySymbol } from '../../utils/currency';
import { formatItemPriceWithConversion } from '../../utils/checkDisplay';
import { resolveShowRemoveButton } from '../../utils/checkDetailViewHelpers';
import { formatDateTime } from '../../utils/dateFormat';
import { getClientTabPath } from '../../utils/navigationReturn';
import ExportPdfButton from '../ExportPdfButton';
import ExportCsvButton from '../ExportCsvButton';
import OrderItemNotes from '../OrderItemNotes';
import ClientOrderItemCard from '../clientAccount/ClientOrderItemCard';
import {
  buildCheckBalanceSummaryRows,
  buildCheckPdfExchangeRateLines,
  buildCheckPdfExportTable,
  buildCheckGrossPdfExportTable,
} from '../../utils/checkDisplay';
import { toggleClientItemsPageSelection, sumFinalPriceByCurrency, formatTotalsByCurrency } from '../../utils/clientItemsSelection';
import { isOrderItemCancellable } from '../../utils/orderItemCancel';
import { formatCurrency } from '../../utils/currency';
import { getCheckConversionCurrencies } from '../../utils/checkExchangeRatesForm';
import { buttonStartIcon, iconButtonContent } from '../../utils/buttonLoading';

/**
 * @param {object} props
 * @param {object} props.check
 * @param {object} props.balance
 * @param {Array} props.items
 * @param {Array} props.transactions
 * @param {boolean} props.isMobile
 * @param {boolean} props.isClient
 * @param {boolean} props.isManager
 * @param {boolean} [props.canRemoveCheckItem=false] – dedicated permission required to remove check items
 * @param {boolean} props.isInternal
 * @param {boolean} props.canEditOrderItemPrice
 * @param {boolean} [props.canEditOrderItemWeight=false]
 * @param {boolean} props.canManageOrders
 * @param {boolean} [props.canChangeStatus=false]
 * @param {function} [props.onMarkDelivered]
 * @param {boolean} [props.showOrderColumn=false]
 * @param {boolean} [props.compactBalance=false]
 * @param {React.ReactNode} [props.headerSlot]
 * @param {function} props.onAddPayment
 * @param {function} [props.onPayAndClose]
 * @param {function} props.onCancelPayment
 * @param {function} props.onRemoveItem
 * @param {function} props.onOpenItemAuditLog
 * @param {function} props.onSaveItemEdit
 * @param {object} props.itemEdits
 * @param {function} props.setItemEdit
 * @param {string|null} props.savingItemId
 * @param {function} [props.isActionPending]
 * @param {function} props.getItemStatusColor
 * @param {function} [props.getItemStatusChipLabel]
 * @param {string} props.itemsSectionTitle
 * @param {string} props.noItemsMessage
 * @param {string} props.transactionsSectionTitle
 * @param {string} props.emptyTransactionsMessage
 * @param {'inTable'|'standalone'} [props.desktopTransactionsEmptyMode='inTable']
 * @param {object} [props.emptyTransactionsStandaloneSx]
 * @param {boolean} [props.canEditExchangeRates=false]
 * @param {function} [props.onCheckUpdated]
 * @param {object} [props.orderLinkClientId] – when set, order links stay under the client URL
 * @param {boolean} [props.canApplyCheckDiscount=false]
 * @param {function} [props.onCheckDiscountUpdated]
 * @param {string} [props.checksBasePath='/api/checks']
 * @param {boolean} [props.canCancelCheckItems=false] – change_status; bulk cancel on check items
 * @param {function} [props.onBulkRemoveItems]
 * @param {function} [props.onBulkCancelItems]
 */
function CheckDetailView({
  check,
  balance,
  items,
  transactions,
  isMobile,
  isClient,
  isManager,
  canRemoveCheckItem = false,
  isInternal,
  canEditOrderItemPrice,
  canEditOrderItemWeight = false,
  canManageOrders,
  canChangeStatus = false,
  onMarkDelivered,
  showOrderColumn = false,
  compactBalance = false,
  headerSlot,
  onAddPayment,
  onPayAndClose,
  onCancelPayment,
  onRemoveItem,
  onOpenItemAuditLog,
  onSaveItemEdit,
  itemEdits = {},
  setItemEdit,
  savingItemId,
  isActionPending,
  getItemStatusColor,
  getItemStatusChipLabel,
  itemsSectionTitle,
  noItemsMessage,
  transactionsSectionTitle,
  emptyTransactionsMessage,
  desktopTransactionsEmptyMode = 'inTable',
  emptyTransactionsStandaloneSx,
  canEditExchangeRates = false,
  onCheckUpdated,
  orderLinkClientId,
  embeddedInClient = false,
  canApplyCheckDiscount = false,
  onCheckDiscountUpdated,
  checksBasePath = '/api/checks',
  canCancelCheckItems = false,
  onBulkRemoveItems,
  onBulkCancelItems,
}) {
  const { t } = useTranslation();
  const emptyDash = t('common.emptyValue');
  const clientDetailChecksLayout = showOrderColumn;
  const priceEditableOnCheckItems = canEditOrderItemPrice && !isClient;

  const showActionsCell = clientDetailChecksLayout ? (isManager || canChangeStatus) : !isClient;
  const showAuditButton = clientDetailChecksLayout ? (isInternal && isManager) : !isClient;
  const showRemoveButton = resolveShowRemoveButton({
    showOrderColumn,
    canRemoveCheckItem,
    isClient,
    checkStatus: check.status,
  });

  const showPaymentInTransactionsRow = canManageOrders && check.status !== CHECK_STATUSES.CLOSED && (
    embeddedInClient || !showOrderColumn || !isMobile
  );
  const amountDue = Number(balance?.amountDue ?? 0);
  const showPayAndClose = showPaymentInTransactionsRow && amountDue > 0 && onPayAndClose;
  const payAndCloseLabel = showOrderColumn ? t('clients.payAndClose') : t('cashiering.payAndClose');

  const checkItemsSelectable = clientDetailChecksLayout && !isClient
    && check.status !== CHECK_STATUSES.CLOSED
    && (showRemoveButton || canCancelCheckItems);

  const [selectedCheckItemIds, setSelectedCheckItemIds] = useState(() => new Set());

  useEffect(() => {
    setSelectedCheckItemIds(new Set());
  }, [check?._id]);

  useEffect(() => {
    setSelectedCheckItemIds((prev) => {
      if (prev.size === 0) return prev;
      const validIds = new Set((items || []).map((i) => i._id));
      const next = new Set([...prev].filter((id) => validIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [items]);

  const selectedCheckItems = useMemo(
    () => (items || []).filter((i) => selectedCheckItemIds.has(i._id)),
    [items, selectedCheckItemIds],
  );

  const selectedCheckSubtotalLabel = useMemo(
    () => formatTotalsByCurrency(sumFinalPriceByCurrency(selectedCheckItems), formatCurrency),
    [selectedCheckItems],
  );

  const allCheckItemsSelected = (items?.length || 0) > 0
    && (items || []).every((i) => selectedCheckItemIds.has(i._id));
  const someCheckItemsSelected = selectedCheckItemIds.size > 0 && !allCheckItemsSelected;

  const toggleCheckItemSelect = useCallback((itemId) => {
    setSelectedCheckItemIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  }, []);

  const toggleSelectAllCheckItems = useCallback(() => {
    setSelectedCheckItemIds(toggleClientItemsPageSelection(items || [], selectedCheckItemIds));
  }, [items, selectedCheckItemIds]);

  const selectedCancellableCount = useMemo(
    () => selectedCheckItems.filter(isOrderItemCancellable).length,
    [selectedCheckItems],
  );

  const pdfTableHelpers = {
    getOrderItemDescriptionCell,
    getItemDetails,
    getItemPricePerCt,
    getItemCarat,
    isCustomByWeightItem,
    weightUnitShort,
    emptyDash,
  };

  const formatCheckItemPrice = (item, edits) => {
    if (balance?.showDualCurrency && item?.currency && balance?.checkCurrency
      && item.currency !== balance.checkCurrency) {
      return formatItemPriceWithConversion(item, balance);
    }
    return getItemDisplayPrice(item, edits);
  };

  const itemCurrencySymbol = (item) => getCurrencySymbol(resolveItemCurrencyCode(item));

  const statusChipLabel = (item) => (getItemStatusChipLabel ? getItemStatusChipLabel(item) : item.status);

  const renderCheckItemCheckbox = (item) => (
    checkItemsSelectable ? (
      <Checkbox
        size="small"
        checked={selectedCheckItemIds.has(item._id)}
        onChange={() => toggleCheckItemSelect(item._id)}
        sx={{ p: 0, mr: 0.5 }}
        inputProps={{ 'aria-label': t('checks.selectItem') }}
      />
    ) : null
  );

  const canMarkDelivered = (item) =>
    canChangeStatus
    && onMarkDelivered
    && (item.status === ORDER_STATUSES.APPROVED || item.status === ORDER_STATUSES.READY);

  const renderDeliverAction = (item) => {
    const pending = isActionPending?.(`deliver-${item._id}`);
    return canMarkDelivered(item) ? (
      <Tooltip title={t('orders.markAsDelivered')}>
        <span>
          <IconButton
            size="small"
            color="warning"
            onClick={() => onMarkDelivered(item)}
            disabled={pending}
          >
            {iconButtonContent(pending, <DeliverActionIcon />)}
          </IconButton>
        </span>
      </Tooltip>
    ) : null;
  };

  const renderRemoveAction = (item) => {
    const pending = isActionPending?.(`remove-${item._id}`);
    return (
      <Tooltip title={t('cashiering.removeFromCheck')}>
        <IconButton
          size="small"
          color="warning"
          onClick={() => onRemoveItem(item._id)}
          disabled={pending}
        >
          {iconButtonContent(pending, <RemoveItemIcon fontSize="small" />)}
        </IconButton>
      </Tooltip>
    );
  };

  const getPricePerUnitFieldValue = (item, edits) => {
    if (edits?.pricePerUnit !== undefined) return edits.pricePerUnit;
    if (item.pricePerUnit != null) return item.pricePerUnit;
    const carat = getItemCarat(item);
    if (carat > 0 && item.finalPrice != null) return item.finalPrice / carat;
    return '';
  };

  const renderPricePerUnitCell = (item, edits, { unitSuffix } = {}) => {
    if (priceEditableOnCheckItems) {
      return (
        <Box display="inline-flex" alignItems="center" gap={0.5} flexWrap="wrap">
          <Typography component="span" color="text.secondary">{itemCurrencySymbol(item)}</Typography>
          <TextField
            size="small"
            type="number"
            inputProps={{ min: 0, step: 'any' }}
            value={getPricePerUnitFieldValue(item, edits)}
            onChange={(e) => setItemEdit(item._id, 'pricePerUnit', e.target.value)}
            onBlur={() => (edits?.pricePerUnit !== undefined ? onSaveItemEdit(item) : null)}
            disabled={savingItemId === item._id}
            sx={{ width: 100, '& .MuiInputBase-root': { height: 28 }, '& .MuiInputBase-input': { py: '4px', px: '8px' } }}
          />
          {unitSuffix ? <Typography component="span" variant="body2">/{unitSuffix}</Typography> : null}
          {savingItemId === item._id && (
            <CircularProgress size={14} aria-label={t('cashiering.saving')} />
          )}
        </Box>
      );
    }
    const p = getItemPricePerCt(item);
    if (p === emptyDash) return emptyDash;
    return unitSuffix ? `${p}/${unitSuffix}` : p;
  };

  const renderWeightCell = (item, edits) => {
    const isNonCert = item.type === DIAMOND_TYPES.NON_CERTIFIED;
    const showWeightInput = canEditOrderItemWeight && (isNonCert || isCustomByWeightItem(item));
    const weightSuffix = isCustomByWeightItem(item) ? weightUnitShort(item.weightUnit) : 'ct';
    if (showWeightInput) {
      return (
        <Box display="inline-flex" alignItems="center" gap={0.5}>
          <TextField
            size="small"
            type="number"
            inputProps={{ min: 0.01, step: 0.01 }}
            value={edits?.weight !== undefined ? edits.weight : (item.weight ?? '')}
            onChange={(e) => setItemEdit(item._id, 'weight', e.target.value)}
            onBlur={() => (edits?.weight !== undefined ? onSaveItemEdit(item) : null)}
            disabled={savingItemId === item._id}
            sx={{ width: 70, '& .MuiInputBase-root': { height: 28 }, '& .MuiInputBase-input': { py: '4px', px: '8px' } }}
          />
          <span>{weightSuffix}</span>
          {savingItemId === item._id && (
            <CircularProgress size={14} aria-label={t('cashiering.saving')} />
          )}
        </Box>
      );
    }
    return getItemDetails(item);
  };

  const renderClientAccountItems = () => {
    if (!items || items.length === 0) {
      return (
        <Typography color="text.secondary" variant="body2" textAlign="center" sx={{ py: 2, mb: 3 }}>
          {noItemsMessage}
        </Typography>
      );
    }
    return (
      <Stack spacing={1} sx={{ mb: 3 }}>
        {(items || []).map((item) => {
          const edits = itemEdits[item._id];
          return (
            <ClientOrderItemCard
              key={item._id}
              item={item}
              t={t}
              displayPrice={formatCheckItemPrice(item, edits)}
            />
          );
        })}
      </Stack>
    );
  };

  const renderItemsMobile = () => {
    if (clientDetailChecksLayout) {
      if (!items || items.length === 0) {
        return (
          <Typography color="text.secondary" textAlign="center" py={2} mb={2}>{noItemsMessage}</Typography>
        );
      }
      return (
        <Stack spacing={1} sx={{ mb: 2 }}>
          {(items || []).map((item) => {
            const edits = itemEdits[item._id];
            const pricePerUnitSuffix = item.type === DIAMOND_TYPES.CUSTOM
              ? (isCustomByWeightItem(item) ? `/${weightUnitShort(item.weightUnit)}` : '')
              : '/ct';
            return (
              <Card key={item._id} variant="outlined" sx={{
                borderColor: selectedCheckItemIds.has(item._id) ? 'primary.main' : 'divider',
                bgcolor: selectedCheckItemIds.has(item._id) ? 'primary.50' : 'background.paper',
              }}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box flex={1} minWidth={0}>
                      <Box display="flex" gap={0.5} flexWrap="wrap" mb={0.5} alignItems="center">
                        {renderCheckItemCheckbox(item)}
                        <Chip label={getItemTypeLabel(item, t)} size="small" variant="outlined" />
                        <Chip label={statusChipLabel(item)} color={getItemStatusColor(item.status)} size="small" />
                      </Box>
                      <Box fontWeight="bold">
                        {item.type === DIAMOND_TYPES.CERTIFIED ? (
                          <>
                            <CertificateLink certificateNumber={getItemRef(item)} lab={getItemLab(item)} variant="body2" />
                            {getCertifiedDiamondSpecsWithWeight(item) !== emptyDash && (
                              <Typography variant="caption" color="text.secondary" display="block" fontWeight="normal">
                                {getCertifiedDiamondSpecsWithWeight(item)}
                              </Typography>
                            )}
                          </>
                        ) : (
                          <Typography variant="body2" fontWeight="bold">{getItemRef(item)}</Typography>
                        )}
                        <OrderItemNotes item={item} sx={{ mt: 0.25 }} />
                      </Box>
                      <Box mt={0.25}>{renderWeightCell(item, edits)}</Box>
                      {item.createdAt && (
                        <Typography variant="caption" color="text.secondary" display="block" mt={0.25}>
                          {formatDateTime(item.createdAt)}
                        </Typography>
                      )}
                      <Box display="flex" justifyContent="space-between" mt={0.5}>
                        <Typography variant="caption" color="text.secondary">
                          {getItemPricePerCt(item) === emptyDash ? emptyDash : `${getItemPricePerCt(item)}${pricePerUnitSuffix}`}
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">{formatCheckItemPrice(item, edits)}</Typography>
                      </Box>
                    </Box>
                    {showActionsCell && (
                      <Box display="flex" flexDirection="column" gap={0.5} ml={1} flexShrink={0}>
                        {renderDeliverAction(item)}
                        {showAuditButton && (
                          <Tooltip title={t('cashiering.itemAuditLog')}>
                            <IconButton size="small" onClick={() => onOpenItemAuditLog(item)}><HistoryIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        )}
                        {showRemoveButton && renderRemoveAction(item)}
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
        {(!items || items.length === 0) ? (
          <Typography color="text.secondary" variant="body2" textAlign="center" sx={{ py: 2 }}>{noItemsMessage}</Typography>
        ) : (items || []).map((item) => {
          const edits = itemEdits[item._id];
          return (
            <Paper key={item._id} variant="outlined" sx={{ p: 1.5 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  {item.type === DIAMOND_TYPES.CERTIFIED ? (
                    <>
                      <CertificateLink certificateNumber={getItemRef(item)} lab={getItemLab(item)} variant="body2" />
                      {getCertifiedDiamondSpecsWithWeight(item) !== emptyDash && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {getCertifiedDiamondSpecsWithWeight(item)}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Typography variant="body2" fontWeight={600}>{getItemRef(item)}</Typography>
                  )}
                  <OrderItemNotes item={item} sx={{ mt: 0.25 }} />
                  <Box display="flex" gap={1} flexWrap="wrap" mt={0.5}>
                    <Chip label={item.type === DIAMOND_TYPES.CERTIFIED ? t('cashiering.certified') : t('cashiering.nonCertified')} size="small" variant="outlined" />
                    <Chip label={statusChipLabel(item)} color={getItemStatusColor(item.status)} size="small" />
                  </Box>
                  {item.createdAt && (
                    <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                      {formatDateTime(item.createdAt)}
                    </Typography>
                  )}
                  <Box mt={0.25}>{renderWeightCell(item, edits)}</Box>
                  <Box display="flex" gap={2} mt={0.5} flexWrap="wrap">
                    <Typography variant="caption" color="text.secondary" component="div">
                      {t('cashiering.pricePerCt')}: {renderPricePerUnitCell(item, edits)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('cashiering.price')}: <strong>{formatCheckItemPrice(item, edits)}</strong>
                    </Typography>
                  </Box>
                </Box>
                {showActionsCell && (
                  <Box display="flex" gap={0.5} flexShrink={0}>
                    {showAuditButton && (
                      <Tooltip title={t('cashiering.itemAuditLog')}>
                        <IconButton size="small" onClick={() => onOpenItemAuditLog(item)}>
                          <HistoryIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {showRemoveButton && renderRemoveAction(item)}
                  </Box>
                )}
              </Box>
            </Paper>
          );
        })}
      </Box>
    );
  };

  const renderItemsDesktop = () => {
    if (clientDetailChecksLayout) {
      if (!items || items.length === 0) {
        return (
          <Typography color="text.secondary" textAlign="center" py={2} mb={2}>{noItemsMessage}</Typography>
        );
      }
      return (
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {checkItemsSelectable && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={allCheckItemsSelected}
                      indeterminate={someCheckItemsSelected}
                      onChange={toggleSelectAllCheckItems}
                      inputProps={{ 'aria-label': t('clients.selectAllOnPage') }}
                    />
                  </TableCell>
                )}
                <TableCell>{t('orderItems.columns.type')}</TableCell>
                <TableCell>{t('orderItems.columns.description')}</TableCell>
                <TableCell>{t('orderItems.columns.weight')}</TableCell>
                <TableCell>{t('cashiering.pricePerCt')}</TableCell>
                <TableCell>{t('cashiering.price')}</TableCell>
                <TableCell>{t('cashiering.checkStatus')}</TableCell>
                <TableCell>{t('orderItems.columns.date')}</TableCell>
                <TableCell>{t('common.order')}</TableCell>
                {showActionsCell && <TableCell align="right">{t('cashiering.actions')}</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {(items || []).map((item) => {
                const edits = itemEdits[item._id];
                const weightSuffix = isCustomByWeightItem(item) ? weightUnitShort(item.weightUnit) : 'ct';
                return (
                  <TableRow key={item._id} hover selected={selectedCheckItemIds.has(item._id)}>
                    {checkItemsSelectable && (
                      <TableCell padding="checkbox">
                        {renderCheckItemCheckbox(item)}
                      </TableCell>
                    )}
                    <TableCell><Chip label={getItemTypeLabel(item, t)} size="small" variant="outlined" /></TableCell>
                    <TableCell>
                      {item.type === DIAMOND_TYPES.CERTIFIED ? (
                        <Box>
                          <CertificateLink certificateNumber={getItemRef(item)} lab={getItemLab(item)} variant="body2" />
                          {getCertifiedDiamondSpecsWithWeight(item) !== emptyDash && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {getCertifiedDiamondSpecsWithWeight(item)}
                            </Typography>
                          )}
                          <OrderItemNotes item={item} />
                        </Box>
                      ) : (
                        <>
                          {getItemRef(item)}
                          <OrderItemNotes item={item} />
                        </>
                      )}
                    </TableCell>
                    <TableCell>{renderWeightCell(item, edits)}</TableCell>
                    <TableCell>
                      {renderPricePerUnitCell(item, edits, { unitSuffix: weightSuffix })}
                    </TableCell>
                    <TableCell><Typography variant="body2" fontWeight="bold">{formatCheckItemPrice(item, edits)}</Typography></TableCell>
                    <TableCell><Chip label={statusChipLabel(item)} color={getItemStatusColor(item.status)} size="small" /></TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {formatDateTime(item.createdAt) || emptyDash}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {getOrderId(item) ? (
                        <RouterLink
                          to={orderLinkClientId
                            ? getClientTabPath(orderLinkClientId, 'orders', getOrderId(item))
                            : `/orders/${getOrderId(item)}`}
                          style={{ fontFamily: 'monospace', fontSize: '0.75rem', textDecoration: 'none', color: 'inherit', fontWeight: 600 }}
                          onClick={(e) => e.stopPropagation()}>
                          #{String(getOrderId(item)).slice(-6).toUpperCase()}
                        </RouterLink>
                      ) : emptyDash}
                    </TableCell>
                    {showActionsCell && (
                      <TableCell align="right">
                        <Box display="inline-flex" gap={0.5}>
                          {renderDeliverAction(item)}
                          {showAuditButton && (
                            <Tooltip title={t('cashiering.auditLog')}><IconButton size="small" onClick={() => onOpenItemAuditLog(item)}><HistoryIcon fontSize="small" /></IconButton></Tooltip>
                          )}
                          {showRemoveButton && renderRemoveAction(item)}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      );
    }

    return (
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('cashiering.item')}</TableCell>
              <TableCell>{t('cashiering.type')}</TableCell>
              <TableCell>{t('orderItems.columns.weight')}</TableCell>
              <TableCell>{t('cashiering.pricePerCt')}</TableCell>
              <TableCell>{t('cashiering.price')}</TableCell>
              <TableCell>{t('cashiering.checkStatus')}</TableCell>
              <TableCell>{t('orderItems.columns.date')}</TableCell>
              {showActionsCell && <TableCell align="right">{t('cashiering.actions')}</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {(items || []).map((item) => {
              const edits = itemEdits[item._id];
              const primary = getCashieringCheckItemPrimaryLabel(item);
              return (
                <TableRow key={item._id}>
                  <TableCell>
                    {item.type === DIAMOND_TYPES.CERTIFIED ? (
                      <Box>
                        <CertificateLink certificateNumber={getItemRef(item)} lab={getItemLab(item)} variant="body2" />
                        {getCertifiedDiamondSpecsWithWeight(item) !== emptyDash && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {getCertifiedDiamondSpecsWithWeight(item)}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Box component="span" display="flex" alignItems="center" flexWrap="wrap" gap={0.5}>
                        <Typography component="span" variant="body2" fontWeight={600}>
                          {primary.includes(' | ') ? primary.split(' | ')[0] : primary}
                        </Typography>
                        {primary.includes(' | ') && (
                          <Typography component="span" variant="body2" color="text.secondary">
                            {' | '}{primary.split(' | ').slice(1).join(' | ')}
                          </Typography>
                        )}
                      </Box>
                    )}
                    <OrderItemNotes item={item} />
                  </TableCell>
                  <TableCell>
                    <Chip label={item.type === DIAMOND_TYPES.CERTIFIED ? t('cashiering.certified') : t('cashiering.nonCertified')} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>{renderWeightCell(item, edits)}</TableCell>
                  <TableCell>{renderPricePerUnitCell(item, edits)}</TableCell>
                  <TableCell>{formatCheckItemPrice(item, edits)}</TableCell>
                  <TableCell>
                    <Chip label={statusChipLabel(item)} color={getItemStatusColor(item.status)} size="small" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {formatDateTime(item.createdAt) || emptyDash}
                    </Typography>
                  </TableCell>
                  {showActionsCell && (
                    <TableCell align="right">
                      <Box display="flex" gap={0.5} justifyContent="flex-end">
                        {showAuditButton && (
                          <Tooltip title={t('cashiering.itemAuditLog')}>
                            <IconButton size="small" onClick={() => onOpenItemAuditLog(item)}>
                              <HistoryIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {showRemoveButton && renderRemoveAction(item)}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
            {(!items || items.length === 0) && (
              <TableRow>
                <TableCell colSpan={showActionsCell ? 8 : 7} align="center" sx={{ py: 2 }}>
                  <Typography color="text.secondary" variant="body2">{noItemsMessage}</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const buildExportConfig = () => {
    const now = new Date().toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
    const pdfTable = buildCheckPdfExportTable(items, balance, t, pdfTableHelpers);
    const checkName = check?.name || check?._id?.slice(-6) || 'check';
    const dual = balance?.showDualCurrency;
    return {
      title: t('export.checkTitle', { name: checkName }),
      filename: `check-${checkName.replace(/\s+/g, '-')}.pdf`,
      columns: pdfTable.columns,
      rows: pdfTable.rows,
      totalRow: pdfTable.totalRow,
      currencyCode: balance?.checkCurrency,
      generatedMeta: balance?.checkCurrency
        ? t('export.generatedWithCurrency', { date: now, currency: getCurrencySymbol(balance.checkCurrency) })
        : t('export.generated', { date: now }),
      balanceSummaryTitle: t('export.balanceSummary'),
      amountColumnHeader: dual
        ? t('export.amountDefault', { currency: getCurrencySymbol(balance.defaultItemCurrency) })
        : t('export.amount'),
      amountColumnHeaderSecondary: dual
        ? t('export.amountCheck', { currency: getCurrencySymbol(balance.checkCurrency) })
        : null,
      balanceSummaryRows: buildCheckBalanceSummaryRows(balance, t, {
        forPdf: true,
        transactions,
      }),
      exchangeRateFooterTitle: t('export.exchangeRates'),
      exchangeRateFooterLines: buildCheckPdfExchangeRateLines(items, balance, t, check),
    };
  };

  const prepaymentHint = getCheckPrepaymentHint(balance, t);
  usePageNotice(
    `check-prepayment-hint-${check?._id || 'unknown'}`,
    prepaymentHint ? { message: prepaymentHint, severity: 'info', persist: true } : null,
  );
  const conversionCurrencies = getCheckConversionCurrencies(
    items,
    balance?.checkCurrency,
    balance?.defaultItemCurrency,
  );

  const buildGrossExportConfig = () => {
    const now = new Date().toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
    const pdfTable = buildCheckGrossPdfExportTable(items, balance, t, pdfTableHelpers);
    const checkName = check?.name || check?._id?.slice(-6) || 'check';
    return {
      title: t('export.checkItemsGrossTitle', { name: checkName }),
      filename: `check-items-${checkName.replace(/\s+/g, '-')}.pdf`,
      columns: pdfTable.columns,
      rows: pdfTable.rows,
      totalRow: pdfTable.totalRow,
      generatedMeta: balance?.checkCurrency
        ? t('export.generatedWithCurrency', { date: now, currency: getCurrencySymbol(balance.checkCurrency) })
        : t('export.generated', { date: now }),
      exchangeRateFooterTitle: conversionCurrencies.length > 0 ? t('export.exchangeRates') : undefined,
      exchangeRateFooterLines: conversionCurrencies.length > 0
        ? buildCheckPdfExchangeRateLines(items, balance, t, check)
        : undefined,
    };
  };

  const showExchangeRates = check?._id && balance?.checkCurrency && conversionCurrencies.length > 0;
  const exchangeRateCapAmount = !isManager && canEditExchangeRates
    ? balance?.maxExchangeRateChangeAmount
    : null;

  return (
    <Box>
      {headerSlot}
      <CheckBalanceCards
        totalItems={balance?.totalItems}
        totalDeposits={balance?.totalDeposits}
        amountDue={balance?.amountDue}
        subtotal={balance?.subtotal}
        vatAmount={balance?.vatAmount}
        vatPercentage={balance?.vatPercentage}
        pricesIncludeVat={balance?.pricesIncludeVat}
        checkCurrency={balance?.checkCurrency}
        defaultItemCurrency={balance?.defaultItemCurrency}
        showDualCurrency={balance?.showDualCurrency}
        subtotalInDefaultCurrency={balance?.subtotalInDefaultCurrency}
        vatAmountInDefaultCurrency={balance?.vatAmountInDefaultCurrency}
        totalInDefaultCurrency={balance?.totalInDefaultCurrency}
        totalDepositsInDefaultCurrency={balance?.totalDepositsInDefaultCurrency}
        amountDueInDefaultCurrency={balance?.amountDueInDefaultCurrency}
        isMobile={isMobile}
        compact={compactBalance}
        mb={2}
        extraCardCount={showExchangeRates ? conversionCurrencies.length : 0}
      >
        {showExchangeRates && (
          <CheckExchangeRatesEditor
            asCard
            checkId={check._id}
            checkCurrency={balance.checkCurrency}
            defaultItemCurrency={balance.defaultItemCurrency}
            exchangeRates={check.exchangeRates || balance.exchangeRates}
            items={items}
            readOnly={!canEditExchangeRates}
            maxChangeAmount={exchangeRateCapAmount}
            onSaved={onCheckUpdated}
          />
        )}
      </CheckBalanceCards>
      {canApplyCheckDiscount && (
        <CheckDiscountEditor
          checkId={check._id}
          check={check}
          balance={balance}
          isManager={isManager}
          canApplyDiscount={canApplyCheckDiscount}
          onUpdated={onCheckDiscountUpdated}
          checksBasePath={checksBasePath}
        />
      )}

      {selectedCheckItemIds.size > 0 && checkItemsSelectable && (
        <Paper sx={{ p: 1.5, mb: 2, bgcolor: 'primary.50' }}>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Typography variant="body2" fontWeight="bold">
              {t('clients.selectedCountSubtotal', {
                count: selectedCheckItemIds.size,
                subtotal: selectedCheckSubtotalLabel,
              })}
            </Typography>
            {showRemoveButton && onBulkRemoveItems && (
              <Button
                size="small"
                variant="outlined"
                color="warning"
                startIcon={buttonStartIcon(isActionPending?.('bulk-remove-check-items'), RemoveItemIcon)}
                onClick={() => onBulkRemoveItems(Array.from(selectedCheckItemIds))}
                disabled={isActionPending?.('bulk-remove-check-items')}
              >
                {t('checks.removeSelectedFromCheck')}
              </Button>
            )}
            {canCancelCheckItems && onBulkCancelItems && selectedCancellableCount > 0 && (
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={buttonStartIcon(isActionPending?.('bulk-cancel-check-items'), CancelIcon)}
                onClick={() => onBulkCancelItems(selectedCheckItems.filter(isOrderItemCancellable))}
                disabled={isActionPending?.('bulk-cancel-check-items')}
              >
                {t('checks.cancelSelectedItems', { count: selectedCancellableCount })}
              </Button>
            )}
          </Box>
        </Paper>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Typography variant="subtitle2">{itemsSectionTitle}</Typography>
        {items?.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <ExportPdfButton exportConfig={buildExportConfig()} label={t('export.checkPdfBalance')} />
            <ExportCsvButton exportConfig={buildExportConfig()} label={t('export.checkCsvBalance')} />
            {clientDetailChecksLayout && !isClient && (
              <>
                <ExportPdfButton exportConfig={buildGrossExportConfig()} label={t('export.checkItemsGross')} />
                <ExportCsvButton exportConfig={buildGrossExportConfig()} label={t('export.checkItemsGrossCsv')} />
              </>
            )}
          </Stack>
        )}
      </Box>
      {checkItemsSelectable && isMobile && clientDetailChecksLayout && (items?.length || 0) > 0 && (
        <FormControlLabel
          control={(
            <Checkbox
              size="small"
              checked={allCheckItemsSelected}
              indeterminate={someCheckItemsSelected}
              onChange={toggleSelectAllCheckItems}
            />
          )}
          label={t('clients.selectAllOnPage')}
          sx={{ mb: 1 }}
        />
      )}
      {isClient ? renderClientAccountItems() : (isMobile ? renderItemsMobile() : renderItemsDesktop())}

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle2">{transactionsSectionTitle}</Typography>
        {showPaymentInTransactionsRow && (
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={onAddPayment} data-testid="check-add-payment">
              {showOrderColumn ? (isMobile ? t('clients.pay') : t('clients.addPayment')) : t('cashiering.addPayment')}
            </Button>
            {showPayAndClose && (
              <Button
                size="small"
                variant="contained"
                onClick={onPayAndClose}
                data-testid="check-pay-and-close"
              >
                {payAndCloseLabel}
              </Button>
            )}
          </Stack>
        )}
      </Box>
      <CheckTransactionsTable
        transactions={transactions}
        paymentCurrency={balance?.defaultItemCurrency}
        isMobile={isMobile}
        isManager={isManager}
        onCancelPayment={onCancelPayment}
        isActionPending={isActionPending}
        emptyMessage={emptyTransactionsMessage}
        desktopEmptyMode={desktopTransactionsEmptyMode}
        emptyStandaloneSx={emptyTransactionsStandaloneSx}
      />
    </Box>
  );
}

export default CheckDetailView;
